import type {
    AnalyzedPrepStep,
    AnalyzedRecipeTimeline,
    PrepTimelineMode,
    ScheduledPrepSegment,
    ScheduledRecipeTimeline,
} from '../types';
import type { Language } from '../types';
import { getTranslation } from './translations';

const RECIPE_COLORS = [
    '#CBA177',
    '#7B9E89',
    '#8B7EC8',
    '#D4846A',
    '#6B9BD1',
    '#B8860B',
];

export function getRecipeTimelineColor(index: number): string {
    return RECIPE_COLORS[index % RECIPE_COLORS.length];
}

function buildSegments(
    recipe: AnalyzedRecipeTimeline,
    recipeStartMinutes: number
): ScheduledPrepSegment[] {
    const segments: ScheduledPrepSegment[] = [];
    let cursor = recipeStartMinutes;

    recipe.steps.forEach((step: AnalyzedPrepStep) => {
        if (step.activeMinutes > 0) {
            segments.push({
                recipeId: recipe.recipeId,
                recipeName: recipe.recipeName,
                stepIndex: step.stepIndex,
                content: step.content,
                sectionHeader: step.sectionHeader,
                startMinutes: cursor,
                endMinutes: cursor + step.activeMinutes,
                durationMinutes: step.activeMinutes,
                kind: 'active',
            });
            cursor += step.activeMinutes;
        }

        if (step.waitAfterMinutes > 0) {
            segments.push({
                recipeId: recipe.recipeId,
                recipeName: recipe.recipeName,
                stepIndex: step.stepIndex,
                content: step.waitDescription || step.content,
                sectionHeader: step.sectionHeader,
                startMinutes: cursor,
                endMinutes: cursor + step.waitAfterMinutes,
                durationMinutes: step.waitAfterMinutes,
                kind: 'wait',
                waitDescription: step.waitDescription,
            });
            cursor += step.waitAfterMinutes;
        }
    });

    return segments;
}

export interface PrepTimelineSchedule {
    recipes: ScheduledRecipeTimeline[];
    timelineStart: Date;
    timelineEnd: Date;
    totalSpanMinutes: number;
}

export function schedulePrepTimeline(
    analyzedRecipes: AnalyzedRecipeTimeline[],
    mode: PrepTimelineMode,
    options?: {
        now?: Date;
        targetTime?: Date;
        sessionStart?: Date;
        recipeOffsets?: Record<string, number>;
    }
): PrepTimelineSchedule {
    const now = options?.now ?? new Date();
    const offsets = options?.recipeOffsets ?? {};

    if (analyzedRecipes.length === 0) {
        return {
            recipes: [],
            timelineStart: now,
            timelineEnd: now,
            totalSpanMinutes: 0,
        };
    }

    const recipeInputs = analyzedRecipes.map((recipe) => ({
        recipe,
        offset: Math.max(0, Math.round(offsets[recipe.recipeId] ?? 0)),
        duration: recipe.totalElapsedMinutes,
    }));

    if (mode === 'targetTime' && options?.targetTime) {
        const timelineEnd = options.targetTime;
        const startTimes = recipeInputs.map(({ offset, duration }) =>
            timelineEnd.getTime() - (offset + duration) * 60_000
        );
        const timelineStart = new Date(Math.min(...startTimes));
        const originMinutes = timelineStart.getTime() / 60_000;
        const targetEndMinutes = timelineEnd.getTime() / 60_000;

        const recipes: ScheduledRecipeTimeline[] = recipeInputs.map(({ recipe, offset, duration }) => {
            const endMinutes = targetEndMinutes - offset - originMinutes;
            const startMinutes = endMinutes - duration;
            const segments = buildSegments(recipe, startMinutes);
            return {
                recipeId: recipe.recipeId,
                recipeName: recipe.recipeName,
                startMinutes,
                endMinutes,
                totalMinutes: duration,
                segments,
            };
        });

        const totalSpanMinutes = Math.max(
            Math.round((timelineEnd.getTime() - timelineStart.getTime()) / 60_000),
            1
        );

        return { recipes, timelineStart, timelineEnd, totalSpanMinutes };
    }

    let timelineStart: Date;
    if (mode === 'session' && options?.sessionStart) {
        timelineStart = options.sessionStart;
    } else {
        timelineStart = now;
    }

    const totalSpanMinutes = Math.max(
        ...recipeInputs.map(({ offset, duration }) => offset + duration),
        1
    );
    const timelineEnd = new Date(timelineStart.getTime() + totalSpanMinutes * 60_000);

    const recipes: ScheduledRecipeTimeline[] = recipeInputs.map(({ recipe, offset, duration }) => {
        const startMinutes = offset;
        const endMinutes = offset + duration;
        const segments = buildSegments(recipe, startMinutes);
        return {
            recipeId: recipe.recipeId,
            recipeName: recipe.recipeName,
            startMinutes,
            endMinutes,
            totalMinutes: duration,
            segments,
        };
    });

    return { recipes, timelineStart, timelineEnd, totalSpanMinutes };
}

export function formatTimelineMinutes(minutes: number, baseDate: Date): string {
    const date = new Date(baseDate.getTime() + minutes * 60_000);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false });
}

export function formatTimelineDateTime(date: Date, referenceDate: Date = new Date()): string {
    const time = date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false });
    if (isSameCalendarDay(date, referenceDate)) {
        return time;
    }
    const datePart = date.toLocaleDateString([], { day: '2-digit', month: '2-digit', year: 'numeric' });
    return `${datePart} ${time}`;
}

function isSameCalendarDay(a: Date, b: Date): boolean {
    return a.getFullYear() === b.getFullYear()
        && a.getMonth() === b.getMonth()
        && a.getDate() === b.getDate();
}

function startOfCalendarDay(date: Date): Date {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

function isCalendarDayAfter(a: Date, b: Date): boolean {
    return startOfCalendarDay(a).getTime() > startOfCalendarDay(b).getTime();
}

function isCalendarDayBefore(a: Date, b: Date): boolean {
    return startOfCalendarDay(a).getTime() < startOfCalendarDay(b).getTime();
}

export type DayBoundaryNotice = 'startPreviousDay' | 'readyNextDay';

export function getDayBoundaryNotice(
    schedule: PrepTimelineSchedule,
    mode: PrepTimelineMode,
    referenceNow: Date
): DayBoundaryNotice | null {
    if (mode === 'targetTime') {
        if (isCalendarDayBefore(schedule.timelineStart, schedule.timelineEnd)) {
            return 'startPreviousDay';
        }
        return null;
    }

    if (isCalendarDayAfter(schedule.timelineEnd, referenceNow)) {
        return 'readyNextDay';
    }
    return null;
}

export function toDateTimeLocalValue(date: Date): string {
    const pad = (value: number) => String(value).padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

export function parseDateTimeLocalValue(value: string): Date {
    return new Date(value);
}

export function getCurrentTimeLinePercent(
    schedule: PrepTimelineSchedule,
    now: Date
): number | null {
    const span = Math.max(schedule.totalSpanMinutes, 1);
    const elapsedMs = now.getTime() - schedule.timelineStart.getTime();
    const elapsedMinutes = elapsedMs / 60_000;

    if (elapsedMinutes < 0 || elapsedMinutes > span) return null;
    return (elapsedMinutes / span) * 100;
}

export const TIMELINE_LABEL_COLUMN = 'min(36%, 13rem)';
export const TIMELINE_WAIT_HATCH = 'repeating-linear-gradient(45deg, transparent, transparent 4px, rgba(255,255,255,0.35) 4px, rgba(255,255,255,0.35) 8px)';
export const OVERNIGHT_WAIT_THRESHOLD_MINUTES = 8 * 60;

export function isOvernightPrepRecipe(recipe: AnalyzedRecipeTimeline): boolean {
    const hasLongWait = recipe.steps.some(
        (step) => step.waitAfterMinutes >= OVERNIGHT_WAIT_THRESHOLD_MINUTES
    );
    if (hasLongWait) return true;

    const totalWaitMinutes = recipe.steps.reduce((sum, step) => sum + step.waitAfterMinutes, 0);
    return totalWaitMinutes > 0 && recipe.totalElapsedMinutes >= OVERNIGHT_WAIT_THRESHOLD_MINUTES;
}

export function getTimelineSegmentBarStyle(
    startMinutes: number,
    durationMinutes: number,
    spanMinutes: number,
    rtl: boolean,
    color: string,
    kind: 'active' | 'wait',
    isDone = false
): { left: string; width: string; backgroundColor: string; opacity?: number; backgroundImage?: string; filter?: string } {
    if (isDone) {
        return {
            ...getTimelineBarStyle(startMinutes, durationMinutes, spanMinutes, rtl),
            backgroundColor: '#7B9E89',
            opacity: 0.45,
        };
    }

    return {
        ...getTimelineBarStyle(startMinutes, durationMinutes, spanMinutes, rtl),
        backgroundColor: color,
        ...(kind === 'wait'
            ? { opacity: 0.5, backgroundImage: TIMELINE_WAIT_HATCH }
            : {}),
    };
}

export function getTimelineBarStyle(
    startMinutes: number,
    durationMinutes: number,
    spanMinutes: number,
    rtl: boolean
): { left: string; width: string } {
    const startPercent = (startMinutes / Math.max(spanMinutes, 1)) * 100;
    const widthPercent = Math.max((durationMinutes / Math.max(spanMinutes, 1)) * 100, 1.5);

    if (rtl) {
        return {
            left: `${Math.max(0, 100 - startPercent - widthPercent)}%`,
            width: `${widthPercent}%`,
        };
    }

    return {
        left: `${startPercent}%`,
        width: `${widthPercent}%`,
    };
}

export function getCurrentTimeMarkerStyle(
    percent: number,
    rtl: boolean
): { left: string } {
    const chartWidthExpr = `(100% - ${TIMELINE_LABEL_COLUMN})`;
    if (rtl) {
        return {
            left: `calc(${chartWidthExpr} * ${(100 - percent) / 100})`,
        };
    }

    return {
        left: `calc(${TIMELINE_LABEL_COLUMN} + ${chartWidthExpr} * ${percent / 100})`,
    };
}

export function formatDurationLabel(totalMinutes: number, language: Language = 'en'): string {
    const hoursUnit = getTranslation(language, 'card_hours');
    const minsUnit = getTranslation(language, 'card_mins');

    if (totalMinutes < 60) {
        return `${totalMinutes}${minsUnit}`;
    }

    const hours = Math.floor(totalMinutes / 60);
    const mins = totalMinutes % 60;
    if (mins > 0) {
        return `${hours}${hoursUnit} ${mins}${minsUnit}`;
    }
    return `${hours}${hoursUnit}`;
}

export const PREPARED_MEAL_HEAT_MINUTES = 15;

export function isPreparedPlannedMeal(sourcingStatus?: 'buy' | 'pantry' | 'prepared'): boolean {
    return sourcingStatus === 'prepared';
}

export function flattenInstructionSteps(
    instructions: string | import('../types').InstructionStep[],
    defaultSection: string
): { content: string; sectionHeader: string; durationMinutes?: number }[] {
    if (typeof instructions === 'string') {
        return instructions
            .split('\n')
            .map((line) => line.trim())
            .filter(Boolean)
            .map((line) => ({
                sectionHeader: defaultSection,
                content: line.replace(/^\d+[\.\)\-\s]+/, '').trim(),
            }));
    }

    const steps: { content: string; sectionHeader: string; durationMinutes?: number }[] = [];
    let currentSection = defaultSection;
    instructions.forEach((step) => {
        if (step.type === 'header') {
            currentSection = step.content.trim() || defaultSection;
            return;
        }
        const content = step.content.trim();
        if (!content) return;
        steps.push({
            sectionHeader: currentSection,
            content,
            ...(typeof step.durationMinutes === 'number' && step.durationMinutes > 0
                ? { durationMinutes: Math.round(step.durationMinutes) }
                : {}),
        });
    });
    return steps;
}

export function applyInstructionDurationsToTimeline(
    timeline: AnalyzedRecipeTimeline,
    flatSteps: { durationMinutes?: number }[]
): AnalyzedRecipeTimeline {
    const steps = timeline.steps.map((step) => {
        const preset = flatSteps[step.stepIndex]?.durationMinutes;
        if (preset == null || preset <= 0) return step;
        return { ...step, activeMinutes: Math.round(preset) };
    });

    const totalActiveMinutes = steps.reduce((sum, step) => sum + step.activeMinutes, 0);
    const totalElapsedMinutes = steps.reduce(
        (sum, step) => sum + step.activeMinutes + step.waitAfterMinutes,
        0
    );

    return {
        ...timeline,
        steps,
        totalActiveMinutes,
        totalElapsedMinutes,
    };
}

export function buildPreparedMealTimeline(
    recipeId: string,
    recipeName: string,
    stepLabel: string
): AnalyzedRecipeTimeline {
    return {
        recipeId,
        recipeName,
        steps: [{
            stepIndex: 0,
            content: stepLabel,
            sectionHeader: stepLabel,
            activeMinutes: PREPARED_MEAL_HEAT_MINUTES,
            waitAfterMinutes: 0,
        }],
        totalActiveMinutes: PREPARED_MEAL_HEAT_MINUTES,
        totalElapsedMinutes: PREPARED_MEAL_HEAT_MINUTES,
    };
}

export function buildFallbackTimeline(
    recipes: { id: string; name: string; prepTime?: number; steps: { content: string; sectionHeader: string; durationMinutes?: number }[] }[]
): AnalyzedRecipeTimeline[] {
    return recipes.map((recipe) => {
        const stepCount = Math.max(recipe.steps.length, 1);
        const presetTotal = recipe.steps.reduce((sum, step) => sum + (step.durationMinutes ?? 0), 0);
        const totalMinutes = Math.max(recipe.prepTime ?? 20, presetTotal || stepCount * 5);
        const unsetCount = recipe.steps.filter((step) => !step.durationMinutes).length || stepCount;
        const remainingMinutes = Math.max(0, totalMinutes - presetTotal);
        const perUnsetStepActive = unsetCount > 0 ? Math.max(1, Math.floor(remainingMinutes / unsetCount)) : 1;

        const steps: AnalyzedPrepStep[] = (recipe.steps.length > 0 ? recipe.steps : [{ content: recipe.name, sectionHeader: 'Prep' }]).map((step, index) => ({
            stepIndex: index,
            content: step.content,
            sectionHeader: step.sectionHeader,
            activeMinutes: step.durationMinutes && step.durationMinutes > 0
                ? Math.round(step.durationMinutes)
                : perUnsetStepActive,
            waitAfterMinutes: 0,
        }));

        const totalElapsedMinutes = steps.reduce(
            (sum, step) => sum + step.activeMinutes + step.waitAfterMinutes,
            0
        );

        return {
            recipeId: recipe.id,
            recipeName: recipe.name,
            steps,
            totalActiveMinutes: totalElapsedMinutes,
            totalElapsedMinutes,
        };
    });
}
